import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// Size option names come from one place (settings > size_option_names, exposed on
// the Theme global in snippets/scripts.liquid) so the gate cannot silently miss a
// product whose option is spelled differently. The literal list is only a fallback
// for the case where the global has not been rendered yet.
// snippets/scripts.liquid declares `const Theme = {...}` in a classic script, which
// creates a global LEXICAL binding — it is not a property of window, so `window.Theme`
// is undefined here and a window-based lookup would silently fall back forever.
const SIZE_OPTION_NAMES =
  (typeof Theme !== 'undefined' && Theme.sizeOptionNames) || ['size', 'shoe size', 'maat'];
const isSizeOptionName = (name) => SIZE_OPTION_NAMES.includes(String(name || '').trim().toLowerCase());

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * The bar is ALWAYS visible on the product page (conversion decision
 * 2026-07-19); it only steps aside while the cart drawer is open.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {MutationObserver | null} */
  #cartDrawerObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #awaitingAdd = false;

  /** @type {Set<Element>} */
  #buyAreaInView = new Set();

  /** @type {IntersectionObserver | null} */
  #buyAreaObserver = null;

  /** @type {HTMLElement | null} */
  #sizeSheet = null;

  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();

    this.#setupTargetButton();
    this.#setupCartDrawerObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Always-on on phones. On desktop the floating bar sat on top of the
    // "2 PAIRS" option, so there it steps aside while the bundle choice or
    // the real buy button is on screen (29-09).
    this.#setupBuyAreaObserver();
    this.#desktop.addEventListener('change', this.#refreshVisibility, { signal });
    this.#refreshVisibility();
  }

  #setupBuyAreaObserver() {
    const section = this.closest('.shopify-section');
    const targets = section?.querySelectorAll('bundle-tiers-component, product-form-component .product-form-buttons');
    if (!targets?.length || typeof IntersectionObserver !== 'function') return;
    this.#buyAreaObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#buyAreaInView.add(entry.target);
        else this.#buyAreaInView.delete(entry.target);
      }
      this.#refreshVisibility();
    });
    targets.forEach((target) => this.#buyAreaObserver?.observe(target));
  }

  #refreshVisibility = () => {
    // Lily & June: also on phones, the bar steps aside while the real buy button is on screen (it covered it on first view, 21-10).
    const hideForBuyArea = this.#buyAreaInView.size > 0;
    if (this.#isCartDrawerOpen() || hideForBuyArea) this.#hideStickyBar();
    else this.#showStickyBar();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cartDrawerObserver?.disconnect();
    this.#buyAreaObserver?.disconnect();
    this.#closeSizeSheet();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Caches the main add-to-cart button that the sticky bar proxies.
   */
  #setupTargetButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // sizeSelectionRequired can be stale after a section morph, so trust the
    // live picker state: only send the shopper to the size picker when no
    // size is actually checked yet.
    if (this.dataset.sizeSelectionRequired === 'true' && !this.#sizeSelected()) {
      this.#openSizeSheet();
      return;
    }

    // A disabled target swallows the click without dispatching a cart event, so
    // #awaitingAdd and data-puppet would never be reset: the next unrelated cart
    // update would then play this bar's "added" feedback for a product that was
    // never added, and the main button's own animation would stay suppressed.
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) {
      this.#awaitingAdd = false;
      return;
    }
    // Success feedback (added-state + fly-to-cart) only plays after the cart
    // request actually succeeds — see #handleCartAddComplete. A sold-out or
    // inventory-capped add must not pretend the product went into the cart.
    this.#awaitingAdd = true;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Plays the "added to cart" feedback: button state + fly-to-cart animation.
   */
  async #playAddedFeedback() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const sourceVariantPicker = event.target.closest('variant-picker');

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
        const sizeSelectionRequired = newStickyAddToCart.dataset.sizeSelectionRequired;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable ?? 'false';
        this.dataset.sizeSelectionRequired = sizeSelectionRequired ?? 'false';

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        this.#setupTargetButton();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        const explicitSizeSelectionPending =
          sourceVariantPicker instanceof HTMLElement &&
          sourceVariantPicker.dataset.requiresExplicitSize === 'true' &&
          sourceVariantPicker.dataset.sizeSelectionMade !== 'true';
        if (explicitSizeSelectionPending) {
          this.dataset.sizeSelectionRequired = 'true';
          this.dataset.currentVariantId = '';
          this.refs.addToCartButton.disabled = false;
          const buttonText = this.refs.addToCartButton.querySelector(
            '.add-to-cart-text__content > span:first-child'
          );
          if (buttonText) buttonText.textContent = window.LJ_I18N.choose_size;

          const selectedNonSizeOptions = Array.from(sourceVariantPicker.querySelectorAll('input:checked'))
            .filter((option) => !isSizeOptionName(/** @type {HTMLInputElement} */ (option).dataset.optionName))
            .map((option) => /** @type {HTMLInputElement} */ (option).value);
          const variantTitle = this.querySelector('.sticky-add-to-cart__variant');
          if (variantTitle) variantTitle.textContent = [...selectedNonSizeOptions, window.LJ_I18N.choose_size].join(' / ');
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    const wasAwaitingAdd = this.#awaitingAdd;

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise
        .then(() => {
          if (wasAwaitingAdd) this.#playAddedFeedback();
        })
        .catch(() => {})
        .finally(() => {
          this.#awaitingAdd = false;
          resetPuppet();
        });
    } else {
      // CartErrorEvent: the add failed, never play success feedback.
      this.#awaitingAdd = false;
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /** @returns {boolean} */
  #isCartDrawerOpen() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    return Boolean(drawer?.hasAttribute('open') || drawer?.querySelector('dialog[open]'));
  }

  #setupCartDrawerObserver() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) return;

    this.#cartDrawerObserver = new MutationObserver(() => {
      if (this.#isCartDrawerOpen()) {
        if (this.#isStuck) this.#hideStickyBar();
        return;
      }

      // Bring the bar straight back when the drawer closes.
      this.#refreshVisibility();
    });
    this.#cartDrawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    const panel = drawer.querySelector('dialog');
    if (panel) this.#cartDrawerObserver.observe(panel, { attributes: true, attributeFilter: ['open'] });
  }

  /** @returns {boolean} */
  #sizeSelected() {
    const section = this.closest('.shopify-section');
    const checkedInput = Array.from(section?.querySelectorAll('variant-picker input[data-option-name]') ?? []).some(
      (input) =>
        isSizeOptionName(/** @type {HTMLInputElement} */ (input).dataset.optionName) &&
        /** @type {HTMLInputElement} */ (input).checked
    );
    if (checkedInput) return true;
    // Size dropdowns always have a selected option; only an available one counts.
    return this.#sizeSelects(section).some((select) => {
      const option = select.options[select.selectedIndex];
      return !!option && option.value !== '' && option.dataset.available !== 'false';
    });
  }

  /** @returns {HTMLSelectElement[]} */
  #sizeSelects(section) {
    return Array.from(section?.querySelectorAll('variant-picker select') ?? []).filter((select) =>
      Array.from(select.options).some((option) => isSizeOptionName(option.dataset.optionName))
    );
  }

  /**
   * Phones: 75 % of visitors never reached the real Add to cart (Clarity, 29-09).
   * Tapping "Choose a size" in the bar opens a sheet with every size; one tap on
   * a size selects it in the real dropdown and adds that variant to the cart.
   */
  #openSizeSheet() {
    const section = this.closest('.shopify-section');
    const select = this.#sizeSelects(section)[0];
    if (!select) {
      this.#focusSizeSelector();
      return;
    }
    this.#closeSizeSheet();

    const sheet = document.createElement('div');
    sheet.className = 'size-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    const t = window.LJ_I18N;
    sheet.setAttribute('aria-label', t.choose_your_size);
    sheet.innerHTML =
      '<div class="size-sheet__backdrop" data-size-sheet-close></div>' +
      '<div class="size-sheet__panel">' +
      '<div class="size-sheet__head"><p class="size-sheet__title"></p>' +
      '<button type="button" class="size-sheet__close" data-size-sheet-close>&times;</button></div>' +
      '<p class="size-sheet__hint"></p>' +
      '<div class="size-sheet__grid"></div>' +
      '<button type="button" class="size-sheet__chart" data-size-sheet-chart></button>' +
      '</div>';
    sheet.querySelector('.size-sheet__title').textContent = t.choose_your_size;
    sheet.querySelector('.size-sheet__close').setAttribute('aria-label', t.close);
    sheet.querySelector('.size-sheet__hint').textContent = t.size_sheet_hint;
    sheet.querySelector('.size-sheet__chart').textContent = t.size_sheet_chart;

    const grid = /** @type {HTMLElement} */ (sheet.querySelector('.size-sheet__grid'));
    for (const option of Array.from(select.options)) {
      if (!option.value || !isSizeOptionName(option.dataset.optionName)) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-sheet__size';
      button.textContent = option.value;
      button.dataset.size = option.value;
      if (option.dataset.available === 'false') {
        button.disabled = true;
        button.setAttribute('aria-label', option.value + ' – sold out');
      }
      grid.appendChild(button);
    }

    sheet.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-size-sheet-close]')) {
        this.#closeSizeSheet();
        return;
      }
      if (target.closest('[data-size-sheet-chart]')) {
        this.#closeSizeSheet();
        /** @type {HTMLElement | null} */ (section?.querySelector('.size-chart-link__btn') ?? null)?.click();
        return;
      }
      const sizeButton = /** @type {HTMLButtonElement | null} */ (target.closest('.size-sheet__size'));
      if (sizeButton && !sizeButton.disabled) this.#pickSizeAndAdd(sizeButton);
    });
    sheet.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeSizeSheet();
    });

    document.body.appendChild(sheet);
    this.#sizeSheet = sheet;
    requestAnimationFrame(() => {
      sheet.dataset.open = 'true';
      /** @type {HTMLElement | null} */ (sheet.querySelector('.size-sheet__size:not([disabled])'))?.focus({ preventScroll: true });
    });
  }

  #closeSizeSheet() {
    this.#sizeSheet?.remove();
    this.#sizeSheet = null;
  }

  /** @param {HTMLButtonElement} sizeButton */
  async #pickSizeAndAdd(sizeButton) {
    const size = sizeButton.dataset.size || '';
    const section = this.closest('.shopify-section');
    const select = this.#sizeSelects(section)[0];
    if (!select) return;

    this.#sizeSheet?.querySelectorAll('button').forEach((button) => (button.disabled = true));
    sizeButton.dataset.busy = 'true';

    select.value = size;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // The picker refetches the section for the new variant; add only once the
    // real form carries that variant, otherwise the old one would be added.
    const ready = await this.#waitFor(() => {
      const current = this.#sizeSelects(section)[0];
      const option = current && Array.from(current.options).find((o) => o.value === size);
      const form = this.#getProductForm();
      const idInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]') ?? null);
      const button = /** @type {HTMLButtonElement | null} */ (form?.querySelector('[ref="addToCartButton"]') ?? null);
      return Boolean(
        current?.value === size && option?.dataset.variantId && idInput?.value === option.dataset.variantId && button && !button.disabled
      );
    }, 8000);

    this.#closeSizeSheet();
    if (!ready) {
      this.#focusSizeSelector();
      return;
    }
    this.#setupTargetButton();
    this.dataset.sizeSelectionRequired = 'false';
    this.handleAddToCartClick();
  }

  /**
   * @param {() => boolean} check
   * @param {number} timeout
   * @returns {Promise<boolean>}
   */
  #waitFor(check, timeout) {
    return new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        let ok = false;
        try {
          ok = check();
        } catch (error) {
          ok = false;
        }
        if (ok) resolve(true);
        else if (Date.now() - started > timeout) resolve(false);
        else setTimeout(tick, 100);
      };
      tick();
    });
  }

  #focusSizeSelector() {
    const section = this.closest('.shopify-section');
    const sizeInput =
      Array.from(section?.querySelectorAll('variant-picker input[data-option-name]') ?? []).find((input) =>
        isSizeOptionName(input.dataset.optionName)
      ) || this.#sizeSelects(section)[0];
    const selector = sizeInput?.closest('fieldset, .variant-option');
    if (!selector) return;

    selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
    selector.classList.add('variant-option--attention');
    window.setTimeout(() => selector.classList.remove('variant-option--attention'), 1600);
    window.setTimeout(() => sizeInput?.focus({ preventScroll: true }), 450);
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
